import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Typography, Box } from '@mui/material';
import {
  spacing, sizing, display, flexbox,
} from '@mui/system';

const CARD_SHADOWS = [
  'none',
  '0 4px 8px 0 rgba(26, 26, 26, 0.16)',
  '0 12px 32px 0 rgba(26, 26, 26, 0.08)',
  '0 24px 40px 0 rgba(26, 26, 26, 0.08)',
];

const StyledCard = styled.div`
  max-width: 100%;
  ${({ elevation, theme }) => `
    background: ${theme.palette.common.white};
    border-radius: ${theme.shape.borderRadius}px;
    box-shadow: ${CARD_SHADOWS[elevation]};
  `}
  ${spacing}
  ${sizing}
  ${display}
  ${flexbox}
`;

const CardTitle = styled.div`
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.palette.grey[40]};
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: ${({ theme }) => theme.spacing(2, 4)};
  ${({ theme }) => `
    ${theme.breakpoints.up('sm')} {
      padding: ${theme.spacing(2, 6)};
    }
  `}
`;

/**
* Renders a <Card/> component.
* When the swatch is clicked a ColorPicker in a Tooltip will render adjacent to the swatch.
* @param {Object} props
* @param {string} props.elevation - the depth of box-shadow.
* @param {string} props.subtitle - optional subtitle text, dependent on title.
* @param {string} props.title - optional title text.
* @param {JSX.Element} props.children - Card body element.
*/
export const Card = ({
  centerPadding = 6,
  elevation = 1,
  subtitle = '',
  title = '',
  titleComponent = 'h3',
  children,
  bodyProps = {},
  ...cardProps
}) => (
  <StyledCard elevation={elevation} {...cardProps}>
    {title && (
      <CardTitle>
        <Typography variant="subtitle1" component={titleComponent}>{title}</Typography>
        {subtitle && (
          <Typography>
            {subtitle}
          </Typography>
        )}
      </CardTitle>
    )}
    <Box sx={{ p: centerPadding }} {...bodyProps}>{children}</Box>
  </StyledCard>
);

Card.propTypes = {
  centerPadding: PropTypes.number,
  elevation: PropTypes.oneOf([1, 2, 3]),
  subtitle: PropTypes.string,
  title: PropTypes.node,
  titleComponent: PropTypes.oneOf(['h2', 'h3', 'h4', 'h5']),
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
